import { Inject, Injectable, Optional } from '@angular/core';
import { merge, Subject, of, EMPTY, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, filter, withLatestFrom, switchMap, tap } from 'rxjs/operators';
import { MediaQueryService } from '@backbase/ui-ang/media-query-lib';
import { LayoutConfig, LAYOUT_CONFIG_TOKEN } from './layout.model';
import { Router, NavigationEnd } from '@angular/router';

/**
 * Service for managing responsive layout behavior and navigation state.
 *
 * @remarks
 *
 * The {@link LayoutService} handles different navigation patterns based on screen size:
 *
 * Navigation Type Horizontal:
 * - On large desktop show the menu in the topbar. On mobile show a side menu
 *
 * Navigation Type Vertical:
 * - On large desktop, show the sidebar. Toggling collapses the nav items into icons.
 * - On tablet and small desktop, collapse the nav items into icons. Toggling shows the full nav items.
 * - On mobile, don't collapse the sidebar, but hide it. Toggling shows the sidebar.
 *
 * The service provides observables for tracking screen resolution changes and navigation
 * expansion state, automatically handling responsive behavior based on configured breakpoints.
 * This service integrates with the {@link MediaQueryService} to monitor viewport changes and
 * automatically adjusts navigation behavior. It combines responsive breakpoints with
 * manual user interactions to provide a seamless navigation experience across all devices.
 * The service handles route navigation events to automatically collapse mobile navigation
 * after route changes, improving user experience.
 *
 * @example
 * ```typescript
 * import { LayoutService } from '@backbase/ui-ang/layout';
 *
 * function setupLayoutService(layoutService: LayoutService) {
 *   // Subscribe to navigation expansion state
 *   layoutService.navigationExpanded$.subscribe(isExpanded => {
 *     console.log('Navigation expanded:', isExpanded);
 *   });
 *
 *   // Subscribe to high resolution view changes
 *   layoutService.isHighResolutionView$.subscribe(isHighRes => {
 *     console.log('High resolution view:', isHighRes);
 *   });
 *
 *   // Toggle navigation manually
 *   layoutService.toggleNav();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  /**
   * Internal subject for emitting high resolution view state changes.
   */
  private readonly isHighResolutionViewSubject: Subject<boolean> = new Subject<boolean>();

  /**
   * Observable that emits the current high resolution view state.
   *
   * @remarks
   * Emits `true` when the viewport width is above the configured collapse breakpoint,
   * indicating a high resolution/desktop view. Emits `false` for mobile/tablet views
   * where the layout should be collapsed.
   *
   * This observable is useful for components that need to adapt their behavior
   * based on screen size without directly accessing the media query service.
   *
   * @example
   * ```typescript
   * // Subscribe to high resolution changes
   * this.layoutService.isHighResolutionView$.subscribe(isHighRes => {
   *   const message = isHighRes ? 'Desktop view' : 'Mobile view';
   *   console.log(message);
   * });
   * ```
   */
  readonly isHighResolutionView$: Observable<boolean> = this.isHighResolutionViewSubject.asObservable();

  /**
   * Observable that emits the current navigation expansion state.
   *
   * @remarks
   * This observable combines multiple sources to determine when the navigation
   * should be expanded or collapsed, including screen size changes, manual toggles,
   * and route navigation events. Components can subscribe to this to update their
   * navigation UI accordingly.
   *
   * @example
   * ```typescript
   * // Subscribe to navigation state changes
   * this.layoutService.navigationExpanded$.subscribe(isExpanded => {
   *   const navElement = document.querySelector('.navigation');
   *   navElement?.classList.toggle('expanded', isExpanded);
   * });
   * ```
   */
  readonly navigationExpanded$: Observable<boolean>;

  /**
   * Internal subject for manual navigation toggle events.
   */
  private readonly manuallyExpanded$ = new Subject<boolean>();

  /**
   * Internal flag tracking the current navigation expansion state.
   */
  private isNavExpanded = false;

  constructor(
    private readonly mediaQueryService: MediaQueryService,
    private readonly router: Router,
    @Optional() @Inject(LAYOUT_CONFIG_TOKEN) private readonly config: LayoutConfig,
  ) {
    /**
     * Default breakpoint used by business
     */
    if (!this.config) {
      this.config = { collapseBreakpoint: 'xl' };
    }

    /**
     * Observing the changes for screen resize
     */
    const isLowResolutionView$ = this.mediaQueryService.isMediaBreakpointMatches(
      'max-width',
      this.config.collapseBreakpoint,
    );

    /**
     * In mobile view after navigation to another route the sidebar should collapse
     */
    const collapseOnRouterNav$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      withLatestFrom(isLowResolutionView$),
      switchMap(([, isLowResolutionView]) => (isLowResolutionView ? of(false) : EMPTY)),
    );

    this.navigationExpanded$ = merge(
      isLowResolutionView$.pipe(
        map((isLowResolutionView) => {
          this.isHighResolutionViewSubject.next(!isLowResolutionView);

          return !isLowResolutionView;
        }),
      ),
      collapseOnRouterNav$,
      this.manuallyExpanded$,
    ).pipe(
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
      tap((isExpanded: boolean) => {
        this.isNavExpanded = isExpanded;
      }),
    );
  }

  /**
   * Toggles the navigation panel expansion state.
   *
   * @remarks
   * This method manually toggles the navigation between expanded and collapsed states.
   * When called, it updates the internal state and emits the new value through the
   * `navigationExpanded$` observable, allowing UI components to react to the change.
   *
   * The toggle behavior respects the responsive breakpoints - on mobile devices,
   * toggling will show/hide the navigation panel, while on desktop it will
   * expand/collapse the navigation items.
   *
   * The method works by inverting the current `isNavExpanded` state and then emitting
   * this new state through the `manuallyExpanded$` subject. This emission is picked up
   * by the `navigationExpanded$` observable which combines manual toggles with automatic
   * responsive behavior and route navigation events.
   *
   * @example
   * ```typescript
   * function handleMenuClick(layoutService: LayoutService) {
   *   layoutService.toggleNav();
   *   console.log('Navigation toggled');
   * }
   * ```
   */
  toggleNav() {
    this.isNavExpanded = !this.isNavExpanded;
    this.manuallyExpanded$.next(this.isNavExpanded);
  }
}
